/**
 * Responsibility: Implement entity images behavior for the engine layer.
 * Scope: Owns engine-layer concerns only; cross-layer orchestration is excluded.
 */
// ============================================================
// ENTITY-IMAGES.TS — Lazy image cache for NPCs and items
//
// NPC images: one shared image per character-definition seed
//   Cache key: npc_img_{normalized_seed_hash}
//
// Item images: one shared image per category keyword
//   Cache key: item_img_{category}
//
// Images cached in IndexedDB (same localforage instance as portraits).
// Never calls AI if the image already exists in cache.
// ============================================================

import localforage from 'localforage'
import { generateImage } from '../ai/imagen'
import type { WorldTaxonomy } from "@anyquest/shared-core"
import { getTaxonomyItemCategory, getTaxonomyNpcVisual } from './world-taxonomy'
localforage.config({ name: 'AnyQuest', storeName: 'vision_cache' })
function normalizeKey(s: string): string {
  const base = s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 24) || 'entity'
  let hash = 2166136261
  for (let i = 0; i < s.length; i++) {
    hash ^= s.charCodeAt(i)
    hash = Math.imul(hash, 16777619)
  }
  const hex = (hash >>> 0).toString(16)
  return `${base}_${hex}`
}


// Map item name keywords → category label used for both caching and image prompts
const ITEM_CATEGORIES: [RegExp, string][] = [
  [/sword|blade|dagger|axe|spear|lance|rapier|katana/i, 'weapon_blade'],
  [/bow|crossbow|arrow|quiver/i,                         'weapon_ranged'],
  [/staff|wand|rod|orb|tome|grimoire/i,                  'magical_implement'],
  [/potion|elixir|vial|brew|tincture/i,                  'potion'],
  [/scroll|parchment|manuscript|letter/i,                'scroll'],
  [/shield|buckler|aegis/i,                              'shield'],
  [/armor|mail|plate|robe|cloak/i,                       'armor'],
  [/ring|amulet|pendant|necklace|bracelet/i,             'jewelry'],
  [/coin|gold|silver|treasure|gem|jewel/i,               'treasure'],
  [/key|lockpick/i,                                      'key'],
  [/food|bread|meat|fruit|ration/i,                      'food'],
  [/torch|lantern|candle/i,                              'light_source'],
  [/map|compass/i,                                       'navigation_tool'],
]
function itemCategory(itemName: string, taxonomy?: WorldTaxonomy): string {
  if (taxonomy) {
    const cat = getTaxonomyItemCategory(taxonomy, itemName)
    if (cat) return cat
  }
  const lower = itemName.toLowerCase()
  for (const [pattern, cat] of ITEM_CATEGORIES) {
    if (pattern.test(lower)) return cat
  }
  return 'miscellaneous_item'
}

function npcImagePrompt(definition: string): string {
  return `Single-character fantasy portrait. Canon character definition: ${definition}. Accurate features and vibe from this definition, head-and-shoulders framing, no extra people, no text, cinematic detail.`
}

const GENERIC_NPC_ARCHETYPES: Array<{ re: RegExp; key: string; label: string }> = [
  { re: /\b(guard|sentinel|watchman|watcher|warden)\b/i, key: 'guard', label: 'city guard' },
  { re: /\b(merchant|trader|vendor|shopkeeper|peddler)\b/i, key: 'merchant', label: 'traveling merchant' },
  { re: /\b(villager|commoner|townsfolk|peasant)\b/i, key: 'villager', label: 'village commoner' },
  { re: /\b(bandit|raider|thug|brigand|outlaw)\b/i, key: 'bandit', label: 'bandit' },
  { re: /\b(soldier|mercenary|knight|man[- ]?at[- ]?arms)\b/i, key: 'soldier', label: 'frontline soldier' },
  { re: /\b(priest|cleric|acolyte|monk)\b/i, key: 'cleric', label: 'temple cleric' },
  { re: /\b(scholar|sage|scribe|librarian)\b/i, key: 'scholar', label: 'arcane scholar' },
]
function npcArchetype(definition: string, taxonomy?: WorldTaxonomy): { key: string; label: string } | null {
  if (taxonomy) {
    const match = getTaxonomyNpcVisual(taxonomy, definition)
    if (match) return match
  }
  for (const rule of GENERIC_NPC_ARCHETYPES) {
    if (rule.re.test(definition)) return { key: rule.key, label: rule.label }
  }
  return null
}

function genericNpcImagePrompt(label: string, imageStyle?: string): string {
  const style = imageStyle || 'fantasy'
  return `Single-character ${style} portrait of a ${label}. Generic archetype look, not a named hero, head-and-shoulders framing, no extra people, no text, cinematic detail.`
}

function itemImagePrompt(category: string, imageStyle?: string): string {
  const label = category.replace(/_/g, ' ')
  const style = imageStyle || 'Dark atmospheric, detailed, glowing magical aura if appropriate'
  return `${style} illustration of a ${label}. Black background. No text, no person.`
}

async function getCached(key: string): Promise<string | null> {
  try {
    return await localforage.getItem<string>(key)
  } catch {
    return null
  }
}

async function setCached(key: string, value: string): Promise<void> {
  try {
    await localforage.setItem(key, value)
  } catch { /* quota exceeded — non-fatal */ }
}

/** Get or lazily generate an NPC portrait for the given character-definition seed. */
export async function getNpcImage(definition: string, taxonomy?: WorldTaxonomy): Promise<string | null> {
  const archetype = npcArchetype(definition, taxonomy)
  const key = archetype
    ? `npc_img_generic_${archetype.key}`
    : `npc_img_unique_${normalizeKey(definition)}`
  const cached = await getCached(key)
  if (cached) return cached
  const prompt = archetype
    ? genericNpcImagePrompt(archetype.label, taxonomy?.imageStyle)
    : npcImagePrompt(definition)
  const url = await generateImage(prompt)
  if (url) await setCached(key, url)
  return url
}

/** Get or lazily generate an item image for the given item name. */
export async function getItemImage(itemName: string, taxonomy?: WorldTaxonomy): Promise<string | null> {
  const category = itemCategory(itemName, taxonomy)
  const key = `item_img_${category}`
  const cached = await getCached(key)
  if (cached) return cached
  const url = await generateImage(itemImagePrompt(category, taxonomy?.imageStyle))
  if (url) await setCached(key, url)
  return url
}
